import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  alpha,
  useTheme as useMuiTheme,
  Stack,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useRoom } from "../contexts/RoomContext";
import { RoomType, RoomStatus } from "../types";
import ErrorMessage from "../components/common/ErrorMessage";
import StyledCard from "../components/common/StyledCard";
import StyledTextField from "../components/common/StyledTextField";
import StyledButton from "../components/common/StyledButton";
import {
  AddCircleRounded,
  DescriptionRounded,
  TitleRounded,
  LabelRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";

const CreateRoom: React.FC = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    roomType: RoomType.PUBLIC,
    startTime: null as Date | null,
    endTime: null as Date | null,
    tags: [] as string[],
  });
  const [formErrors, setFormErrors] = useState({
    title: "",
    description: "",
    startTime: "",
    endTime: "",
  });
  const [tagInput, setTagInput] = useState("");
  const { createRoom, loading, error } = useRoom();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useMuiTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData({ ...formData, roomType: e.target.value as RoomType });
  };

  const handleDateChange = (name: string, value: Date | null) => {
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
      valid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
      valid = false;
    }

    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
      valid = false;
    }

    if (!formData.endTime) {
      newErrors.endTime = "End time is required";
      valid = false;
    }

    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = "End time must be after start time";
        valid = false;
      }
    }

    setFormErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const roomData = {
        ...formData,
        startTime: formData.startTime!,
        endTime: formData.endTime!,
      };

      await createRoom(roomData);
      navigate("/dashboard");
    } catch (error) {
      // Error is handled by the room context
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "Select date";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage:
          theme.palette.mode === "light"
            ? `radial-gradient(circle at 10% 20%, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, transparent 40%),
              radial-gradient(circle at 90% 80%, ${alpha(
              theme.palette.secondary.main,
              0.05
            )} 0%, transparent 40%)`
            : `radial-gradient(circle at 10% 20%, ${alpha(
              theme.palette.primary.main,
              0.08
            )} 0%, transparent 40%),
              radial-gradient(circle at 90% 80%, ${alpha(
              theme.palette.secondary.main,
              0.08
            )} 0%, transparent 40%)`,
        py: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container component="main" maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box mb={4} textAlign="center" id="create-room-header">
          <Typography
            component="h1"
            variant="h3"
            sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1.5,
              letterSpacing: "-0.02em",
            }}
          >
            Create New Room
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Design your virtual space and preview it in real-time
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start">
          {/* Left Side: Form */}
          <Grid item xs={12} md={7} lg={8}>
            <StyledCard
              sx={{
                background:
                  theme.palette.mode === "light"
                    ? `linear-gradient(145deg, ${alpha(
                      theme.palette.background.paper,
                      0.9
                    )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                    : `linear-gradient(145deg, ${alpha(
                      theme.palette.background.paper,
                      0.95
                    )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                backdropFilter: "blur(20px)",
                p: { xs: 3, sm: 4 },
                "&:hover": {
                  transform: "none",
                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                  "&::before": {
                    opacity: 0,
                  },
                },
              }}
              id="create-room-form"
            >
              <ErrorMessage message={error} />

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <StyledTextField
                      required
                      fullWidth
                      label="Room Title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      error={!!formErrors.title}
                      helperText={formErrors.title}
                      placeholder="Enter an engaging title..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: alpha(theme.palette.primary.main, 0.15), // Reset to default
                            borderWidth: "1.5px",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: <TitleRounded sx={{ color: "text.secondary", mr: 1, opacity: 0.7 }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <StyledTextField
                      required
                      fullWidth
                      multiline
                      rows={4}
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      error={!!formErrors.description}
                      helperText={formErrors.description}
                      placeholder="Describe what your room is about..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: alpha(theme.palette.primary.main, 0.15), // Reset to default
                            borderWidth: "1.5px",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: <DescriptionRounded sx={{ color: "text.secondary", mr: 1, opacity: 0.7, mt: 1, alignSelf: 'flex-start' }} />,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Room Type</InputLabel>
                      <Select
                        value={formData.roomType}
                        label="Room Type"
                        onChange={handleSelectChange}
                        sx={{
                          borderRadius: 3,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(theme.palette.divider, 0.2),
                          },
                        }}
                      >
                        <MenuItem value={RoomType.PUBLIC}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>🌍</span>
                            <Typography>Public Room</Typography>
                          </Stack>
                        </MenuItem>
                        <MenuItem value={RoomType.PRIVATE}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>🔒</span>
                            <Typography>Private Room</Typography>
                          </Stack>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Add Tags"
                      value={tagInput}
                      onChange={handleTagInputChange}
                      placeholder="Type & press Enter"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: alpha(theme.palette.primary.main, 0.15), // Reset to default
                            borderWidth: "1.5px",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: <LabelRounded sx={{ color: "text.secondary", mr: 1, opacity: 0.7 }} />,
                        endAdornment: (
                          <Button
                            onClick={handleAddTag}
                            disabled={!tagInput.trim()}
                            size="small"
                            sx={{
                              minWidth: "auto",
                              px: 2,
                              borderRadius: 2,
                              background: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              "&:hover": {
                                background: alpha(theme.palette.primary.main, 0.1), // Reset to default
                              },
                            }}
                          >
                            Add
                          </Button>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DateTimePicker
                      label="Start Time"
                      value={formData.startTime}
                      onChange={(newValue) => handleDateChange("startTime", newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!formErrors.startTime,
                          helperText: formErrors.startTime,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DateTimePicker
                      label="End Time"
                      value={formData.endTime}
                      onChange={(newValue) => handleDateChange("endTime", newValue)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!formErrors.endTime,
                          helperText: formErrors.endTime,
                        },
                      }}
                    />
                  </Grid>

                  {formData.tags.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {formData.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            onDelete={() => handleRemoveTag(tag)}
                            sx={{
                              background: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              "& .MuiChip-deleteIcon": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Box
                  sx={{
                    mt: 6,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <StyledButton
                    type="button"
                    variant="outlined"
                    onClick={() => navigate("/dashboard")}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: alpha(theme.palette.text.secondary, 0.2),
                      color: "text.secondary",
                      "&:hover": {
                        borderColor: alpha(theme.palette.text.secondary, 0.2),
                        color: "text.secondary",
                        background: "transparent",
                      },
                    }}
                  >
                    Cancel
                  </StyledButton>
                  <StyledButton
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={<AddCircleRounded />}
                    sx={{
                      px: 5,
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 600,
                      fontSize: "1rem",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      "&:hover": {
                        transform: "none",
                        boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      },
                    }}
                  >
                    {loading ? "Creating..." : "Create Room"}
                  </StyledButton>
                </Box>
              </Box>
            </StyledCard>
          </Grid>

          {/* Right Side: Live Preview */}
          <Grid item xs={12} md={5} lg={4}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 24 } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.secondary",
                  fontWeight: 600,
                  mb: 2,
                  pl: 1,
                }}
              >
                <VisibilityRounded fontSize="small" /> Live Preview
              </Typography>

              <StyledCard
                sx={{
                  position: "relative",
                  overflow: "visible",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "none",
                    boxShadow: theme.palette.mode === "dark"
                      ? `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`
                      : `0 8px 32px ${alpha(theme.palette.common.black, 0.05)}`,
                    "&::before": {
                      opacity: 0,
                    },
                  },
                }}
                id="create-room-preview"
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Preview Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2.5,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.05
                      )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                      borderTopLeftRadius: "inherit",
                      borderTopRightRadius: "inherit",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      fontWeight="700"
                      noWrap
                      sx={{ maxWidth: "70%", opacity: formData.title ? 1 : 0.5 }}
                    >
                      {formData.title || "Room Title"}
                    </Typography>
                    <Chip
                      label="SCHEDULED"
                      size="small"
                      sx={{
                        background: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        px: 1,
                      }}
                    />
                  </Box>

                  {/* Preview Body */}
                  <Box sx={{ p: 2.5 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        Created by: <span style={{ color: theme.palette.primary.main }}>{user?.username || "You"}</span>
                      </Typography>
                      <Chip
                        label={formData.roomType === RoomType.PRIVATE ? "PRIVATE" : "PUBLIC"}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: alpha(
                            formData.roomType === RoomType.PRIVATE
                              ? theme.palette.error.main
                              : theme.palette.success.main,
                            0.1
                          ),
                          color:
                            formData.roomType === RoomType.PRIVATE
                              ? theme.palette.error.main
                              : theme.palette.success.main,
                        }}
                      />
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        minHeight: "40px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        mb: 2,
                        lineHeight: 1.6,
                        opacity: formData.description ? 1 : 0.5,
                        fontStyle: formData.description ? "normal" : "italic",
                      }}
                    >
                      {formData.description || "Your room description will appear here..."}
                    </Typography>

                    <Divider sx={{ my: 2, borderStyle: "dashed" }} />

                    <Stack spacing={1.5}>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>Start</span>
                        <span style={{ color: theme.palette.text.secondary }}>{formatDate(formData.startTime)}</span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                      >
                        <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>End</span>
                        <span style={{ color: theme.palette.text.secondary }}>{formatDate(formData.endTime)}</span>
                      </Typography>
                    </Stack>

                    {formData.tags.length > 0 ? (
                      <Box mt={2.5} display="flex" flexWrap="wrap" gap={0.5}>
                        {formData.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              fontSize: "0.7rem",
                              height: 24,
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box mt={2.5} sx={{ opacity: 0.5 }}>
                        <Chip
                          label="Tags will appear here"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 24 }}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2.5, pt: 0 }}>
                  <StyledButton
                    size="medium"
                    variant="contained"
                    fullWidth
                    disabled
                    sx={{
                      borderRadius: 3,
                      py: 1.2,
                      fontWeight: 600,
                      opacity: 0.7,
                    }}
                  >
                    Join Room
                  </StyledButton>
                </CardActions>
              </StyledCard>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CreateRoom;
